'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronsUpDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/cn'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
    variant?: 'default' | 'detailed'
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      'border-b',
      variant === 'default' && 'bg-white shadow-sm rounded-lg mb-4',
      className,
    )}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    variant?: 'default' | 'detailed'
    status?: string
  }
>(({ className, children, variant = 'default', status, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'bg-white border border-system-border-primary flex flex-1 w-full h-12 rounded-lg items-center justify-between py-3 px-4 font-inter font-medium text-base transition-all text-system-text-primary',
        variant === 'default' && 'bg-gray-200',
        className,
      )}
      {...props}
    >
      <span className="flex items-center gap-2">
        {children}
        {status && (
          <span className="rounded-2xl border border-system-content-orange px-2 py-1 text-sm text-system-content-orange">
            {status}
          </span>
        )}
      </span>
      <ChevronsUpDown className="size-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    variant?: 'default' | 'custom'
  }
>(({ className, children, variant = 'default', ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'bg-system-background-grey w-full overflow-hidden pt-4 px-5 font-inter transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      variant === 'custom' && 'bg-gray-200 px-4',
      className,
    )}
    {...props}
  >
    <div className={cn('pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
